import { Box } from "@mui/material"
import { useParams } from "@tanstack/react-router"
import type { FC } from "react"
import { useCallback, useMemo, useState } from "react"
import { FormProvider } from "react-hook-form"

import AddProductDialog from "../components/AddProductDialog"

import { CompanyStatus, type Product } from "@/graphql/codegen/graphql"
import { queryClient } from "@/providers/GraphqlRouterProvider"
import { COMPANY_EDIT_ROUTE } from "@/screens/Companies/constants"
import { useCompanyEditSearch } from "@/screens/Companies/hooks"
import { CompanyProductsFilterChips } from "@/screens/Companies/products/CompanyProductsFilterChips"
import { useAddProduct } from "@/screens/Companies/products/hooks/useAddProduct"
import { ProductFilterDrawer } from "@/screens/Companies/products/ProductFilterDrawer"
import { ProductListTable } from "@/screens/Companies/products/ProductListTable"
import { ProductsMultipleShipingLocationsAlert } from "@/screens/Companies/products/ProductsMultipleShipingLocationsAlert"
import { useProductSearchStore } from "@/screens/Companies/products/stores/useProductsSearchStore"
import { useCompanyStore } from "@/screens/Companies/stores/useCompanyStore"
import { CustomerProductsContainer } from "@/screens/CustomerProducts/containers/CustomerProductsContainer"
import { usePurchaseOrderDialog } from "@/screens/Procurement/hooks/usePurchaseOrderDialog"
import { PurchaseOrderDialog } from "@/screens/Procurement/PurchaseOrder/components/dialogs/PurchaseOrderDialog"
import { hasValidShipFromLocations } from "@/screens/Procurement/PurchaseOrder/utils/common"
import CompanyProductsSearchBar from "@/screens/Products/components/CompanyProductsSearchBar"
import { CUSTOMER_COMPANY_TYPES, type CompanyType } from "@/types/companyType"

const CompanyProductsContainer: FC = () => {
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false)
  const { companyId } = useParams({ from: COMPANY_EDIT_ROUTE })
  const currentSearch = useCompanyEditSearch()

  const { query, statuses } = currentSearch

  const company = useCompanyStore((state) => state.company)
  const companyTypes = company?.types ?? []

  const { open, setOpen, methods, handleSubmit } = useAddProduct({ companyId })

  const toggleFilterDrawer = useCallback(() => setIsFilterDrawerOpen((prev) => !prev), [])

  const [selected, setSelected] = useState<Record<string, boolean>>({})
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([])

  const data = useProductSearchStore(({ data }) => data)
  const fetchingProducts = useProductSearchStore(({ fetching }) => fetching)
  const error = useProductSearchStore(({ error }) => error)
  const executeSearch = useProductSearchStore(({ executeSearch }) => executeSearch)
  const loadMore = useProductSearchStore(({ loadMore }) => loadMore)

  const handleSelectionUpdate = useCallback((products: Product[]) => {
    setSelectedProducts(products)
  }, [])

  const hasMultipleShippingLocationsSelected = useMemo(() => {
    const shippingIds = selectedProducts
      .map((product) => product.locationsAssociations?.[0]?.shippingId)
      .filter(Boolean)

    return new Set(shippingIds).size > 1
  }, [selectedProducts])

  const {
    open: purchaseOrderDialogOpen,
    fetching,
    handleOpen,
    handleClose,
    handleSubmit: handlePurchaseOrderSubmit,
  } = usePurchaseOrderDialog(selectedProducts)

  const isActiveCompany = company?.status === CompanyStatus.Active
  const hasData = data.length > 0
  const hasFilters = (query?.length && query.length > 0) || (statuses?.length && statuses.length > 0)

  const shouldShowProductSearchBar = isActiveCompany && (hasData || Boolean(hasFilters))

  const handleAddProductSubmit = async (data: any) => {
    await handleSubmit(data)
    // Refresh the product list after adding a new product
    executeSearch?.(queryClient, currentSearch, true)
  }

  const hasInvalidShipFromLocations = useMemo(() => {
    return !hasValidShipFromLocations(selectedProducts)
  }, [selectedProducts])

  const isStartPoDisabled =
    selectedProducts.length === 0 || hasMultipleShippingLocationsSelected || hasInvalidShipFromLocations

  const isCustomerCompanyType = companyTypes?.some((item) => CUSTOMER_COMPANY_TYPES.includes(item as CompanyType))

  return (
    <Box className='flex h-full flex-col gap-4'>
      {!isCustomerCompanyType && (
        <>
          <CompanyProductsSearchBar
            isCustomerCompanyType={isCustomerCompanyType}
            toggleFilterDrawer={toggleFilterDrawer}
            shouldShowProductSearchBar={shouldShowProductSearchBar}
            onNewProduct={() => setOpen(true)}
            onStartPo={handleOpen}
            isStartPoDisabled={isStartPoDisabled}
          />

          <CompanyProductsFilterChips />

          {hasMultipleShippingLocationsSelected && <ProductsMultipleShipingLocationsAlert />}
        </>
      )}

      {company && (
        <Box className='grow overflow-hidden'>
          {isCustomerCompanyType ? (
            <CustomerProductsContainer />
          ) : (
            <ProductListTable
              company={company}
              methods={methods}
              open={open}
              data={data}
              fetchingProducts={fetchingProducts}
              error={error}
              executeSearch={executeSearch}
              loadMore={loadMore}
              setOpen={setOpen}
              selected={selected}
              setSelected={setSelected}
              onSelectionUpdate={handleSelectionUpdate}
            />
          )}
        </Box>
      )}

      <FormProvider {...methods}>
        <AddProductDialog
          open={open}
          onClose={() => {
            setOpen(false)
          }}
          onSubmit={handleAddProductSubmit}
        />
      </FormProvider>
      <ProductFilterDrawer showFilterDrawer={isFilterDrawerOpen} toggleDrawer={toggleFilterDrawer} />
      {company && purchaseOrderDialogOpen && (
        <PurchaseOrderDialog
          onClose={() => handleClose()}
          fetching={fetching}
          company={company}
          products={selectedProducts}
          onSubmit={handlePurchaseOrderSubmit}
        />
      )}
    </Box>
  )
}

export default CompanyProductsContainer
